import { useRef, useState, useCallback, useEffect } from 'react';
import { Upload, Trash2, Loader2, ImageIcon } from 'lucide-react';
import { Button } from '@/ui/primitives/button';
import {
  useActivityImageUpload,
  ActivityImageUploadError,
} from '@/features/activities/hooks/useActivityImageUpload';
import { activityImageFieldCopy as copy } from './activity-image-field-copy';

interface ActivityImageFieldProps {
  value: string | null;
  onChange: (url: string | null) => void;
  businessId: number;
  disabled?: boolean;
  onPendingCleanup?: (urls: string[]) => void;
}

/**
 * Image field with the following lifecycle:
 *
 * - `persistedUrl` = whatever was passed in the initial `value` (i.e., what's in DB).
 *   The parent form is responsible for deleting it after successful submit if it differs
 *   from the current value.
 *
 * - `sessionUploads` = list of URLs uploaded during this dialog session. When a new upload
 *   replaces a previous session upload, the previous one is deleted immediately (orphan
 *   prevention). When the dialog closes without saving, the parent should delete all
 *   uncommitted session uploads — call onPendingCleanup with the list.
 */
export default function ActivityImageField({
  value,
  onChange,
  businessId,
  disabled = false,
  onPendingCleanup,
}: ActivityImageFieldProps) {
  const { uploadImage, deleteImage } = useActivityImageUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [localNotice, setLocalNotice] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [sessionUploads, setSessionUploads] = useState<string[]>([]);
  const dragDepth = useRef(0);

  const acceptsDrop = !disabled && !isUploading;

  // A file dropped anywhere but on the zone makes the browser navigate to it, which throws the
  // partner out of a half-filled form.
  useEffect(() => {
    const swallowDrop = (event: DragEvent) => event.preventDefault();
    window.addEventListener('dragover', swallowDrop);
    window.addEventListener('drop', swallowDrop);
    return () => {
      window.removeEventListener('dragover', swallowDrop);
      window.removeEventListener('drop', swallowDrop);
    };
  }, []);

  const openPicker = useCallback(() => {
    setLocalError(null);
    setLocalNotice(null);
    fileInputRef.current?.click();
  }, []);

  const upload = useCallback(
    async (file: File) => {
      setIsUploading(true);
      setLocalError(null);
      try {
        const newUrl = await uploadImage(file, businessId);

        // Orphan cleanup: if there's a previous session upload, delete it immediately
        setSessionUploads((prev) => {
          const next = [...prev, newUrl];
          if (prev.length > 0) {
            const orphan = prev[prev.length - 1];
            deleteImage(orphan).catch(() => {});
          }
          onPendingCleanup?.(next);
          return next;
        });

        onChange(newUrl);
      } catch (err) {
        if (err instanceof ActivityImageUploadError) {
          setLocalError(err.message);
        } else {
          setLocalError(copy.uploadFailed);
          console.error(err);
        }
      } finally {
        setIsUploading(false);
      }
    },
    [uploadImage, deleteImage, businessId, onChange, onPendingCleanup]
  );

  const handleFileSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = ''; // allow re-picking same file later
      if (!file) return;
      setLocalNotice(null);
      void upload(file);
    },
    [upload]
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      dragDepth.current += 1;
      if (acceptsDrop && e.dataTransfer.types.includes('Files')) setIsDragOver(true);
    },
    [acceptsDrop]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = acceptsDrop ? 'copy' : 'none';
    },
    [acceptsDrop]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      dragDepth.current = 0;
      setIsDragOver(false);
      if (!acceptsDrop) return;

      const files = Array.from(e.dataTransfer.files);
      if (files.length === 0) {
        setLocalNotice(null);
        setLocalError(copy.notAFile);
        return;
      }

      setLocalNotice(files.length > 1 ? copy.onlyFirstFileTaken : null);
      void upload(files[0]);
    },
    [acceptsDrop, upload]
  );

  const handleRemove = useCallback(() => {
    // Clear the value; cleanup of the actual storage object is the parent's responsibility
    // (handled on successful form submit when persistedUrl differs from current value).
    onChange(null);
    setLocalError(null);
    setLocalNotice(null);
  }, [onChange]);

  return (
    <div
      className="space-y-2"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-image-drop-zone=""
      data-drag-over={isDragOver ? 'true' : undefined}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFileSelected}
        disabled={disabled || isUploading}
      />

      {value ? (
        <div className="relative mx-auto w-full max-w-sm">
          <div
            className={`relative aspect-square overflow-hidden rounded-md border bg-muted transition-colors ${
              isDragOver ? 'border-primary ring-2 ring-primary' : ''
            }`}
          >
            <img
              src={value}
              alt={copy.previewAlt}
              className="h-full w-full object-cover"
            />
            {isDragOver && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-background/80 text-sm font-medium text-primary">
                {copy.dropHere}
              </div>
            )}
            {isUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/60">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            )}
          </div>
          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={openPicker}
              disabled={disabled || isUploading}
            >
              <Upload className="mr-2 h-4 w-4" />
              {copy.replace}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleRemove}
              disabled={disabled || isUploading}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {copy.remove}
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          disabled={disabled || isUploading}
          className={`mx-auto flex aspect-square w-full max-w-sm flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-4 transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
            isDragOver
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted/50'
          }`}
        >
          {isUploading ? (
            <Loader2 className="h-8 w-8 animate-spin" />
          ) : isDragOver ? (
            <>
              <ImageIcon className="h-8 w-8" />
              <span className="text-sm font-medium">{copy.dropHere}</span>
            </>
          ) : (
            <>
              <ImageIcon className="h-8 w-8" />
              <span className="text-sm">{copy.pickerLabel}</span>
              <span className="text-xs">{copy.dragHint}</span>
              <span className="text-xs">{copy.constraints}</span>
            </>
          )}
        </button>
      )}

      {localError && (
        <p role="alert" className="text-sm text-destructive">
          {localError}
        </p>
      )}
      {localNotice && !localError && (
        <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
          {localNotice}
        </p>
      )}
    </div>
  );
}
